import { Image as ImageIcon } from "lucide-react";
import { rarityMeta } from "@/lib/itemMeta";

// <ItemFrame> composes a rarity-aware backdrop behind any transparent item
// image. All visuals come from `rarityMeta(rarity).backdrop` so a single
// config change propagates everywhere (inventory, marketplace, loot boxes,
// item details, etc).
//
// Layer stack (bottom → top):
//   1. base solid color (per rarity)
//   2. radial gradient (per rarity)
//   3. conic energy ring (Epic+ only, slowly rotating)
//   4. starfield particles (Rare+ only)
//   5. the item image, centered with object-contain
//   6. inset rarity ring + outer glow
//
// Props:
//   rarity   string             item rarity key (Common, Uncommon, ...)
//   image    string | null      already-resolved URL of the transparent asset
//   alt      string             alt text
//   children React.ReactNode    overlays (chips, badges) placed above the frame
//   className extra Tailwind classes on the outer aspect-square wrapper
//   imgClassName classes on the <img> for hover effects etc.
export default function ItemFrame({
  rarity,
  image,
  alt = "",
  children,
  className = "",
  imgClassName = "",
}) {
  const r = rarityMeta(rarity);
  const b = r.backdrop;

  return (
    <div
      className={`relative aspect-square overflow-hidden ${className}`}
      style={{
        // Solid base + radial gradient form the static backdrop.
        background: `radial-gradient(circle at 50% 42%, ${b.radialA} 0%, ${b.radialB} 70%), ${b.base}`,
      }}
    >
      {/* Layer 3: conic energy ring (Epic / Legendary / Mythic) */}
      {b.energy && (
        <div
          aria-hidden
          className="absolute inset-[-20%] item-energy-spin opacity-80 pointer-events-none"
          style={{ background: b.energy, animationDuration: `${b.pulseMs || 4000}ms` }}
        />
      )}

      {/* Layer 4: particle starfield */}
      {b.particles !== "none" && (
        <div
          aria-hidden
          className={`absolute inset-0 pointer-events-none item-particles-${b.particles}`}
          style={{ "--p-color": r.color }}
        />
      )}

      {/* Layer 5: the transparent item asset, centered */}
      {image ? (
        <img
          src={image}
          alt={alt}
          loading="lazy"
          className={`relative z-10 w-full h-full object-contain p-[10%] drop-shadow-[0_6px_18px_rgba(0,0,0,0.55)] ${imgClassName}`}
        />
      ) : (
        <div className="relative z-10 w-full h-full flex items-center justify-center">
          <ImageIcon className="w-10 h-10 text-[var(--t-text-muted)]" />
        </div>
      )}

      {/* Layer 6: rarity ring */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none ring-1 ring-inset"
        style={{
          boxShadow: `inset 0 0 24px ${r.glow}`,
          // The ring-1 ring-inset only sets the border-color via a custom property
          // when `--tw-ring-color` is overridden — we use the inline box-shadow for the glow
          // and a simple absolute border below for the crisp rarity outline.
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none border"
        style={{ borderColor: r.color, opacity: 0.85 }}
      />

      {/* Caller-provided overlays (rarity chip, type chip, etc.) */}
      {children}
    </div>
  );
}
